import './VehiclesLibraryDrawer.css';
import type {
  VehicleManifestEntry,
  VehicleCategory,
} from '../../services/host/VehiclesServiceModule';
import type { ModificationManifestEntry } from '../../services/host/VehicleModificationsServiceModule';
import type {
  VehiclesLibraryState,
  VehiclesLibraryTab,
} from '../../hooks/useVehiclesLibrary';

/**
 * VehiclesLibraryDrawer — 2026-06-08 Phase 2E wire-up for the canonical
 * Q Manual VG35002 vehicles + vehicle-modifications catalogs.
 *
 * Option A drawer layout per PRE-COMPACT-18 §5.4 (user-approved): 4 internal
 * tabs (Land / Air / Water / Modifications). Land/Air/Water render the 92
 * vehicles grouped by subcategory inside their tab. Modifications renders
 * the 31 mods as a flat list (no subcategory groupings — Q Manual organizes
 * mods by function, not by host-vehicle category).
 *
 * 3-state union per useVehiclesLibrary: each tab renders loading/error/
 * ready independently of the others (so Modifications tab can be ready
 * while Vehicles is still loading; useful when the user opens the drawer
 * mid-mount).
 *
 * Mirrors EquipmentLibraryDrawer.tsx pattern (Phase 1E gold standard) plus
 * a tab-strip header. The detail expansion sub-component shape is shared
 * via the VehicleDetail + ModificationDetail components below.
 */

const TAB_DEFS: ReadonlyArray<{ key: VehiclesLibraryTab; label: string }> = [
  { key: 'land', label: 'Land' },
  { key: 'air', label: 'Air' },
  { key: 'water', label: 'Water' },
  { key: 'modifications', label: 'Modifications' },
];

interface SubcategoryDef {
  key: string;
  label: string;
}

const LAND_SUBCATEGORIES: ReadonlyArray<SubcategoryDef> = [
  { key: 'automobile', label: 'Automobiles' },
  { key: 'miscellaneous_land', label: 'Miscellaneous Land' },
];

const AIR_SUBCATEGORIES: ReadonlyArray<SubcategoryDef> = [
  { key: 'jet_aircraft', label: 'Jet Aircraft' },
  { key: 'piston_aircraft', label: 'Piston Aircraft' },
  { key: 'rotary_aircraft', label: 'Rotary Aircraft' },
  { key: 'special_air_device', label: 'Special Air Devices' },
];

const WATER_SUBCATEGORIES: ReadonlyArray<SubcategoryDef> = [
  { key: 'surface_boat', label: 'Surface Boats' },
  { key: 'underwater_craft', label: 'Underwater Craft' },
  { key: 'underwater_device', label: 'Underwater Devices' },
];

const TAB_TO_CATEGORY: Record<
  Exclude<VehiclesLibraryTab, 'modifications'>,
  VehicleCategory
> = {
  land: 'land_vehicle',
  air: 'air_vehicle',
  water: 'water_vehicle',
};

const TAB_TO_SUBCATEGORIES: Record<
  Exclude<VehiclesLibraryTab, 'modifications'>,
  ReadonlyArray<SubcategoryDef>
> = {
  land: LAND_SUBCATEGORIES,
  air: AIR_SUBCATEGORIES,
  water: WATER_SUBCATEGORIES,
};

export interface VehiclesLibraryDrawerProps {
  vehicles: VehicleManifestEntry[];
  vehiclesState: VehiclesLibraryState;
  vehiclesError?: string | null;
  modifications: ModificationManifestEntry[];
  modificationsState: VehiclesLibraryState;
  modificationsError?: string | null;
  activeTab: VehiclesLibraryTab;
  onTabChange: (tab: VehiclesLibraryTab) => void;
  selectedVehicleId: string | null;
  selectedVehicleData: Record<string, unknown> | null;
  selectedVehicleState: VehiclesLibraryState;
  onSelectVehicle: (vehicleId: string) => void;
  selectedModificationId: string | null;
  selectedModificationData: Record<string, unknown> | null;
  selectedModificationState: VehiclesLibraryState;
  onSelectModification: (modId: string) => void;
  onClose?: () => void;
  testidPrefix?: string;
}

function formatStat(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(' / ');
  return String(value);
}

function VehicleDetail({
  data,
  testidPrefix,
}: {
  data: Record<string, unknown>;
  testidPrefix: string;
}): JSX.Element {
  const stats = (data['stats'] ?? {}) as Record<string, unknown>;
  const description = (data['description'] as string) ?? '';
  const qEvaluation = (data['q_evaluation'] as string) ?? '';
  const gameInformation = (data['game_information'] as string) ?? '';
  const page = data['page'] as number | undefined;

  const STAT_ORDER: ReadonlyArray<{ key: string; label: string }> = [
    { key: 'pm', label: 'PM' },
    { key: 'red', label: 'RED' },
    { key: 'crus', label: 'CRUS' },
    { key: 'max', label: 'MAX' },
    { key: 'rge', label: 'RGE' },
    { key: 'fce', label: 'FCE' },
    { key: 'str', label: 'STR' },
    { key: 'cost_gbp', label: 'COST (£)' },
  ];
  const known = new Set(STAT_ORDER.map((s) => s.key));
  const extras = Object.keys(stats).filter((k) => !known.has(k));

  return (
    <div
      className="br-vehicles-detail"
      data-testid={`${testidPrefix}-vehicle-detail`}
    >
      <h3 className="br-vehicles-detail-title">
        {(data['name'] as string) ?? '—'}
      </h3>
      {page !== undefined && (
        <p
          className="br-vehicles-detail-page br-label"
          data-testid={`${testidPrefix}-vehicle-detail-page`}
        >
          Q Manual p.{page}
        </p>
      )}
      <dl className="br-vehicles-detail-stats">
        {STAT_ORDER.map((s) =>
          s.key in stats ? (
            <div key={s.key}>
              <dt>{s.label}</dt>
              <dd className="br-mono">{formatStat(stats[s.key])}</dd>
            </div>
          ) : null,
        )}
        {extras.map((k) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd className="br-mono">{formatStat(stats[k])}</dd>
          </div>
        ))}
      </dl>
      {description !== '' && (
        <p
          className="br-vehicles-detail-text"
          data-testid={`${testidPrefix}-vehicle-detail-description`}
        >
          {description}
        </p>
      )}
      {qEvaluation !== '' && (
        <p
          className="br-vehicles-detail-text br-vehicles-detail-q"
          data-testid={`${testidPrefix}-vehicle-detail-q-evaluation`}
        >
          <em>Q evaluation:</em> {qEvaluation}
        </p>
      )}
      {gameInformation !== '' && (
        <p
          className="br-vehicles-detail-text br-vehicles-detail-game"
          data-testid={`${testidPrefix}-vehicle-detail-game-info`}
        >
          <strong>Game info:</strong> {gameInformation}
        </p>
      )}
    </div>
  );
}

function ModificationDetail({
  data,
  testidPrefix,
}: {
  data: Record<string, unknown>;
  testidPrefix: string;
}): JSX.Element {
  const description = (data['description'] as string) ?? '';
  const qEvaluation = (data['q_evaluation'] as string) ?? '';
  const gameInformation = (data['game_information'] as string) ?? '';
  const page = data['page'] as number | undefined;
  const structurePoints = data['structure_points'];
  const costPerStr = data['cost_gbp_per_str'];
  const costFlat = data['cost_gbp'];

  return (
    <div
      className="br-vehicles-detail br-modification-detail"
      data-testid={`${testidPrefix}-modification-detail`}
    >
      <h3 className="br-vehicles-detail-title">
        {(data['name'] as string) ?? '—'}
      </h3>
      {page !== undefined && (
        <p
          className="br-vehicles-detail-page br-label"
          data-testid={`${testidPrefix}-modification-detail-page`}
        >
          Q Manual p.{page}
        </p>
      )}
      <dl className="br-vehicles-detail-stats">
        <div>
          <dt>STR</dt>
          <dd className="br-mono">{formatStat(structurePoints)}</dd>
        </div>
        {costPerStr !== undefined && costPerStr !== null && (
          <div>
            <dt>£/STR</dt>
            <dd className="br-mono">{formatStat(costPerStr)}</dd>
          </div>
        )}
        {costFlat !== undefined && costFlat !== null && (
          <div>
            <dt>COST (£)</dt>
            <dd className="br-mono">{formatStat(costFlat)}</dd>
          </div>
        )}
      </dl>
      {description !== '' && (
        <p
          className="br-vehicles-detail-text"
          data-testid={`${testidPrefix}-modification-detail-description`}
        >
          {description}
        </p>
      )}
      {qEvaluation !== '' && (
        <p
          className="br-vehicles-detail-text br-vehicles-detail-q"
          data-testid={`${testidPrefix}-modification-detail-q-evaluation`}
        >
          <em>Q evaluation:</em> {qEvaluation}
        </p>
      )}
      {gameInformation !== '' && (
        <p
          className="br-vehicles-detail-text br-vehicles-detail-game"
          data-testid={`${testidPrefix}-modification-detail-game-info`}
        >
          <strong>Game info:</strong> {gameInformation}
        </p>
      )}
    </div>
  );
}

function VehiclesTabContent({
  tab,
  vehicles,
  selectedVehicleId,
  selectedVehicleData,
  selectedVehicleState,
  onSelectVehicle,
  testidPrefix,
}: {
  tab: Exclude<VehiclesLibraryTab, 'modifications'>;
  vehicles: VehicleManifestEntry[];
  selectedVehicleId: string | null;
  selectedVehicleData: Record<string, unknown> | null;
  selectedVehicleState: VehiclesLibraryState;
  onSelectVehicle: (vehicleId: string) => void;
  testidPrefix: string;
}): JSX.Element {
  const category = TAB_TO_CATEGORY[tab];
  const tabVehicles = vehicles.filter((v) => v.category === category);
  if (tabVehicles.length === 0) {
    return (
      <p
        className="br-vehicles-library-empty"
        data-testid={`${testidPrefix}-tab-${tab}-empty`}
      >
        No {tab} vehicles in seed.
      </p>
    );
  }

  const subcategories = TAB_TO_SUBCATEGORIES[tab];

  return (
    <div
      className="br-vehicles-library-list"
      data-testid={`${testidPrefix}-tab-${tab}-list`}
    >
      {subcategories.map((subDef) => {
        const items = tabVehicles.filter((v) => v.subcategory === subDef.key);
        if (items.length === 0) return null;
        return (
          <section
            key={subDef.key}
            className="br-vehicles-library-group"
            data-subcategory={subDef.key}
            data-testid={`${testidPrefix}-group-${subDef.key}`}
          >
            <h4 className="br-vehicles-library-group-title">
              {subDef.label} ({items.length})
            </h4>
            <ul className="br-vehicles-library-rows">
              {items.map((v) => {
                const isSelected = v.id === selectedVehicleId;
                return (
                  <li
                    key={v.id}
                    className="br-vehicles-library-row-wrapper"
                  >
                    <button
                      type="button"
                      className={
                        isSelected
                          ? 'br-vehicles-library-row br-vehicles-library-row-selected'
                          : 'br-vehicles-library-row'
                      }
                      data-testid={`${testidPrefix}-vehicle-row-${v.id}`}
                      data-selected={isSelected ? 'true' : 'false'}
                      onClick={() => onSelectVehicle(v.id)}
                      aria-pressed={isSelected}
                    >
                      <span className="br-vehicles-library-row-title">
                        {v.name}
                      </span>
                      {v.page !== null && (
                        <span className="br-vehicles-library-row-meta br-label">
                          p.{v.page}
                        </span>
                      )}
                    </button>
                    {isSelected && selectedVehicleState === 'loading' && (
                      <p
                        className="br-vehicles-library-detail-loading"
                        data-testid={`${testidPrefix}-vehicle-detail-loading`}
                      >
                        Loading…
                      </p>
                    )}
                    {isSelected && selectedVehicleState === 'error' && (
                      <p
                        className="br-vehicles-library-detail-error"
                        data-testid={`${testidPrefix}-vehicle-detail-error`}
                      >
                        Could not load vehicle.
                      </p>
                    )}
                    {isSelected &&
                      selectedVehicleState === 'ready' &&
                      selectedVehicleData !== null && (
                        <VehicleDetail
                          data={selectedVehicleData}
                          testidPrefix={testidPrefix}
                        />
                      )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function ModificationsTabContent({
  modifications,
  selectedModificationId,
  selectedModificationData,
  selectedModificationState,
  onSelectModification,
  testidPrefix,
}: {
  modifications: ModificationManifestEntry[];
  selectedModificationId: string | null;
  selectedModificationData: Record<string, unknown> | null;
  selectedModificationState: VehiclesLibraryState;
  onSelectModification: (modId: string) => void;
  testidPrefix: string;
}): JSX.Element {
  if (modifications.length === 0) {
    return (
      <p
        className="br-vehicles-library-empty"
        data-testid={`${testidPrefix}-tab-modifications-empty`}
      >
        No vehicle modifications in seed.
      </p>
    );
  }

  return (
    <div
      className="br-vehicles-library-list"
      data-testid={`${testidPrefix}-tab-modifications-list`}
    >
      <ul className="br-vehicles-library-rows">
        {modifications.map((m) => {
          const isSelected = m.id === selectedModificationId;
          return (
            <li
              key={m.id}
              className="br-vehicles-library-row-wrapper"
            >
              <button
                type="button"
                className={
                  isSelected
                    ? 'br-vehicles-library-row br-vehicles-library-row-selected'
                    : 'br-vehicles-library-row'
                }
                data-testid={`${testidPrefix}-modification-row-${m.id}`}
                data-selected={isSelected ? 'true' : 'false'}
                onClick={() => onSelectModification(m.id)}
                aria-pressed={isSelected}
              >
                <span className="br-vehicles-library-row-title">{m.name}</span>
                {m.structure_points !== null && (
                  <span className="br-vehicles-library-row-meta br-label">
                    {typeof m.structure_points === 'number'
                      ? `${m.structure_points} STR`
                      : `${m.structure_points}`}
                  </span>
                )}
              </button>
              {isSelected && selectedModificationState === 'loading' && (
                <p
                  className="br-vehicles-library-detail-loading"
                  data-testid={`${testidPrefix}-modification-detail-loading`}
                >
                  Loading…
                </p>
              )}
              {isSelected && selectedModificationState === 'error' && (
                <p
                  className="br-vehicles-library-detail-error"
                  data-testid={`${testidPrefix}-modification-detail-error`}
                >
                  Could not load modification.
                </p>
              )}
              {isSelected &&
                selectedModificationState === 'ready' &&
                selectedModificationData !== null && (
                  <ModificationDetail
                    data={selectedModificationData}
                    testidPrefix={testidPrefix}
                  />
                )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function VehiclesLibraryDrawer({
  vehicles,
  vehiclesState,
  vehiclesError,
  modifications,
  modificationsState,
  modificationsError,
  activeTab,
  onTabChange,
  selectedVehicleId,
  selectedVehicleData,
  selectedVehicleState,
  onSelectVehicle,
  selectedModificationId,
  selectedModificationData,
  selectedModificationState,
  onSelectModification,
  onClose,
  testidPrefix = 'br-vehicles-library',
}: VehiclesLibraryDrawerProps) {
  const isModsTab = activeTab === 'modifications';
  const showVehiclesLoading = !isModsTab && vehiclesState === 'loading';
  const showVehiclesError = !isModsTab && vehiclesState === 'error';
  const showModsLoading = isModsTab && modificationsState === 'loading';
  const showModsError = isModsTab && modificationsState === 'error';

  // Count for the header badge: matches the active tab's domain.
  const headerCount = isModsTab
    ? modifications.length
    : vehicles.filter(
        (v) => v.category === TAB_TO_CATEGORY[activeTab],
      ).length;

  return (
    <aside
      className="br-scenario-drawer br-vehicles-library-drawer"
      data-testid={testidPrefix}
      data-state={isModsTab ? modificationsState : vehiclesState}
      data-active-tab={activeTab}
    >
      <header className="br-vehicles-library-header">
        <span className="br-vehicles-library-title">Vehicles Library</span>
        <span
          className="br-vehicles-library-count br-label"
          data-testid={`${testidPrefix}-count`}
        >
          {headerCount}
        </span>
        {onClose !== undefined && (
          <button
            type="button"
            className="br-vehicles-library-close"
            data-testid={`${testidPrefix}-close`}
            onClick={onClose}
            aria-label="Close vehicles library"
          >
            ✕
          </button>
        )}
      </header>

      <div
        className="br-vehicles-library-tabs"
        role="tablist"
        data-testid={`${testidPrefix}-tabs`}
      >
        {TAB_DEFS.map((tabDef) => {
          const isActive = tabDef.key === activeTab;
          return (
            <button
              key={tabDef.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`${testidPrefix}-tab-${tabDef.key}`}
              data-active={isActive ? 'true' : 'false'}
              className={
                isActive
                  ? 'br-vehicles-library-tab br-vehicles-library-tab-active'
                  : 'br-vehicles-library-tab'
              }
              onClick={() => onTabChange(tabDef.key)}
            >
              {tabDef.label}
            </button>
          );
        })}
      </div>

      <p
        className="br-vehicles-library-banner"
        data-testid={`${testidPrefix}-banner`}
      >
        Q Manual VG35002 vehicles + modifications catalog — 92 vehicles
        across Land (58), Air (20), and Water (14) plus 31 vehicle
        modifications (Q-Branch mod-budget math: total mod-Points ≤ host
        vehicle's STR). Click a row to expand its stat block + Q evaluation
        + game info.
      </p>

      {showVehiclesLoading && (
        <p
          className="br-vehicles-library-loading"
          data-testid={`${testidPrefix}-loading`}
        >
          Loading vehicles…
        </p>
      )}

      {showVehiclesError && (
        <p
          className="br-vehicles-library-error"
          data-testid={`${testidPrefix}-error`}
        >
          Failed to load vehicles: {vehiclesError ?? 'unknown error'}
        </p>
      )}

      {showModsLoading && (
        <p
          className="br-vehicles-library-loading"
          data-testid={`${testidPrefix}-modifications-loading`}
        >
          Loading modifications…
        </p>
      )}

      {showModsError && (
        <p
          className="br-vehicles-library-error"
          data-testid={`${testidPrefix}-modifications-error`}
        >
          Failed to load modifications: {modificationsError ?? 'unknown error'}
        </p>
      )}

      {!isModsTab && vehiclesState === 'ready' && (
        <VehiclesTabContent
          tab={activeTab}
          vehicles={vehicles}
          selectedVehicleId={selectedVehicleId}
          selectedVehicleData={selectedVehicleData}
          selectedVehicleState={selectedVehicleState}
          onSelectVehicle={onSelectVehicle}
          testidPrefix={testidPrefix}
        />
      )}

      {isModsTab && modificationsState === 'ready' && (
        <ModificationsTabContent
          modifications={modifications}
          selectedModificationId={selectedModificationId}
          selectedModificationData={selectedModificationData}
          selectedModificationState={selectedModificationState}
          onSelectModification={onSelectModification}
          testidPrefix={testidPrefix}
        />
      )}
    </aside>
  );
}
